import { useState, useEffect, useRef } from "react";

const API_URL = "https://archpoint.onrender.com/api/chat";

const DEFAULT_SUGGESTIONS = [
  "About ArchPoint",
  "Our Services",
  "Projects",
  "Book Consultation",
  "Contact"
];

// Every suggestion chip used anywhere in the flow, deduplicated — shown
// after a handoff reply so the person has every option available rather
// than a narrowed-down guess at what they want next.
const ALL_SUGGESTIONS = [
  "About ArchPoint",
  "Our Services",
  "Architectural Design",
  "Interior Design",
  "Elevation Design",
  "Space Planning",
  "Turnkey Solutions",
  "3D Visualization",
  "Projects",
  "Our Projects",
  "Residential Projects",
  "Commercial Projects",
  "Process & Timeline",
  "Revisions Policy",
  "Pricing",
  "Book Consultation",
  "Contact",
  "Location",
  "Career"
];

// Words/phrases that mean "get me a human" — matched as flexible keyword
// combinations rather than exact phrases, since real phrasing varies a lot
// ("connect me with your team", "can I speak to someone", "get me a real
// person", etc). Handled locally instead of trusting the backend model to
// recognize a handoff request, since giving a wrong answer here is worse
// than giving a slightly generic one.
function isHandoffIntent(msg) {
  const lower = msg.toLowerCase();

  const mentionsConnect = lower.includes("connect");
  const mentionsTalkOrSpeak =
    lower.includes("talk to") ||
    lower.includes("talk with") ||
    lower.includes("speak to") ||
    lower.includes("speak with");
  const mentionsWhoToReach =
    lower.includes("team") ||
    lower.includes("human") ||
    lower.includes("someone") ||
    lower.includes("person") ||
    lower.includes("agent") ||
    lower.includes("representative") ||
    lower.includes("staff") ||
    lower.includes(" you") || // "connect me with you"
    lower.includes("archpoint");

  return (
    (mentionsConnect && mentionsWhoToReach) ||
    (mentionsTalkOrSpeak && mentionsWhoToReach) ||
    lower.includes("real person") ||
    lower.includes("human agent") ||
    lower.includes("human support") ||
    lower.includes("call me") ||
    lower.includes("ring me")
  );
}

const HANDOFF_REPLY =
  "Of course — I'll point you to the team directly. You can reach ArchPoint at ArchPoint.in/contact, where you'll find our phone number, email, and office address, or use the \"Book Consultation\" option below and someone from the team will get back to you.";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hello 👋 Welcome to ArchPoint - where design meets innovation. How can I assist you today?"
    }
  ]);
  const [suggestions, setSuggestions] = useState(DEFAULT_SUGGESTIONS);
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef(null);

  const sendMessage = async (text) => {
    const messageToSend = text || input;
    if (!messageToSend.trim()) return;

    setMessages(prev => [...prev, { sender: "user", text: messageToSend }]);
    setInput("");

    // Handle "connect me to the team" locally and reliably, without
    // depending on the backend model to interpret the request correctly.
    if (isHandoffIntent(messageToSend)) {
      setIsTyping(true);
      setTimeout(() => {
        setMessages(prev => [...prev, { sender: "bot", text: HANDOFF_REPLY }]);
        setIsTyping(false);
        setSuggestions(ALL_SUGGESTIONS);
      }, 400);
      return;
    }

    setIsTyping(true);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: messageToSend })
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      setTimeout(() => {
        setMessages(prev => [...prev, { sender: "bot", text: data.reply }]);
        setIsTyping(false);
        updateSuggestions(messageToSend.toLowerCase());
      }, 500);

    } catch (err) {
      console.error("Error sending message:", err);
      setIsTyping(false);
      setMessages(prev => [
        ...prev,
        {
          sender: "bot",
          text: "⚠️ I'm having trouble connecting to the server. Please check if the backend is running on https://archpoint.onrender.com or contact us directly at ArchPoint.in/contact"
        }
      ]);
    }
  };

  const updateSuggestions = (msg) => {
    if (msg.includes("service") || msg.includes("what do you offer")) {
      setSuggestions([
        "Architectural Design",
        "Interior Design",
        "Space Planning",
        "Turnkey Solutions",
        "Projects"
      ]);
    }
    else if (msg.includes("architectural") || msg.includes("architecture")) {
      setSuggestions([
        "Interior Design",
        "Elevation Design",
        "Our Projects",
        "Book Consultation"
      ]);
    }
    else if (msg.includes("interior")) {
      setSuggestions([
        "Space Planning",
        "3D Visualization",
        "Our Projects",
        "Pricing"
      ]);
    }
    else if (msg.includes("planning") || msg.includes("layout")) {
      setSuggestions([
        "Architectural Design",
        "Turnkey Solutions",
        "Process & Timeline",
        "Pricing"
      ]);
    }
    else if (msg.includes("elevation") || msg.includes("facade")) {
      setSuggestions([
        "Our Projects",
        "3D Visualization",
        "Book Consultation"
      ]);
    }
    else if (msg.includes("turnkey")) {
      setSuggestions([
        "Process & Timeline",
        "Pricing",
        "Book Consultation"
      ]);
    }
    else if (msg.includes("project") || msg.includes("portfolio") || msg.includes("work")) {
      setSuggestions([
        "Residential Projects",
        "Commercial Projects",
        "Book Consultation",
        "Process & Timeline"
      ]);
    }
    else if (msg.includes("residential") || msg.includes("home") || msg.includes("villa")) {
      setSuggestions([
        "Commercial Projects",
        "Interior Design",
        "Book Consultation",
        "Pricing"
      ]);
    }
    else if (msg.includes("commercial") || msg.includes("office")) {
      setSuggestions([
        "Residential Projects",
        "Space Planning",
        "Book Consultation"
      ]);
    }
    else if (msg.includes("consultation") || msg.includes("appointment") || msg.includes("book")) {
      setSuggestions([
        "Contact",
        "Location",
        "Pricing",
        "Process & Timeline"
      ]);
    }
    else if (msg.includes("contact") || msg.includes("phone") || msg.includes("email")) {
      setSuggestions([
        "Location",
        "Book Consultation",
        "Our Services",
        "Career"
      ]);
    }
    else if (msg.includes("location") || msg.includes("address")) {
      setSuggestions([
        "Contact",
        "Book Consultation",
        "Our Services"
      ]);
    }
    else if (msg.includes("price") || msg.includes("cost") || msg.includes("budget")) {
      setSuggestions([
        "Process & Timeline",
        "Book Consultation",
        "Our Services",
        "Projects"
      ]);
    }
    else if (msg.includes("process") || msg.includes("timeline") || msg.includes("how long")) {
      setSuggestions([
        "Pricing",
        "Book Consultation",
        "Revisions Policy",
        "Projects"
      ]);
    }
    else if (msg.includes("sustainable") || msg.includes("green") || msg.includes("eco")) {
      setSuggestions([
        "Our Services",
        "Projects",
        "Book Consultation"
      ]);
    }
    else if (msg.includes("3d") || msg.includes("visualization") || msg.includes("technology")) {
      setSuggestions([
        "Projects",
        "Process & Timeline",
        "Book Consultation"
      ]);
    }
    else if (msg.includes("revision") || msg.includes("change")) {
      setSuggestions([
        "Process & Timeline",
        "Book Consultation",
        "Pricing"
      ]);
    }
    else if (msg.includes("career") || msg.includes("job") || msg.includes("internship")) {
      setSuggestions([
        "Contact",
        "About ArchPoint",
        "Our Services"
      ]);
    }
    else if (msg.includes("help") || msg.includes("options")) {
      setSuggestions([
        "Our Services",
        "Projects",
        "Book Consultation",
        "Contact"
      ]);
    }
    else if (msg.includes("hi") || msg.includes("hello") || msg.includes("hey")) {
      setSuggestions([
        "About ArchPoint",
        "Our Services",
        "Projects",
        "Book Consultation"
      ]);
    }
    else if (msg.includes("thank") || msg.includes("thanks")) {
      setSuggestions([
        "Our Services",
        "Projects",
        "Book Consultation",
        "Contact"
      ]);
    }
    else {
      setSuggestions(DEFAULT_SUGGESTIONS);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-white shadow-2xl hover:shadow-amber-500/50 hover:scale-110 transition-all duration-300 flex items-center justify-center border-2 border-amber-300"
        aria-label="Toggle chat"
      >
        {open ? (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {open && (
        <div className="fixed z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-amber-200 bottom-24 right-4 left-4 h-[70vh] max-h-[600px] sm:left-auto sm:right-6 sm:bottom-28 sm:w-96 sm:h-[550px]">

          <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-white px-5 py-4 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                </svg>
              </div>
              <div className="min-w-0">
                <div className="font-bold text-lg truncate">ArchPoint</div>
                <div className="text-xs text-amber-100 truncate">Design Assistant</div>
              </div>
            </div>
            <button 
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/20 transition-colors flex items-center justify-center shrink-0"
              aria-label="Close chat"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 p-4 overflow-y-auto bg-gradient-to-b from-amber-50/30 to-white space-y-3 min-h-0">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm whitespace-pre-line ${
                    msg.sender === "user"
                      ? "bg-gradient-to-br from-amber-400 to-amber-500 text-white rounded-br-sm"
                      : "bg-white text-gray-800 border border-amber-100 rounded-bl-sm"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-amber-100 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div
            className="p-3 border-t border-amber-100 bg-gradient-to-b from-white to-amber-50/30 max-h-20 overflow-y-auto shrink-0"
            style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
          >
            {suggestions.map((item, i) => (
              <button
                key={i}
                onClick={() => sendMessage(item)}
                style={{ marginRight: "4px", marginBottom: "4px" }}
                className="px-3 py-1.5 text-xs font-medium rounded-full border border-amber-300 text-amber-700 hover:bg-gradient-to-r hover:from-amber-400 hover:to-amber-500 hover:text-white hover:border-amber-400 transition-all duration-200 shadow-sm hover:shadow-md whitespace-nowrap"
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex border-t border-amber-100 bg-white shrink-0">
            <input
              type="text"
              placeholder="Type your message..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
              className="flex-1 min-w-0 px-4 py-3 outline-none text-sm placeholder-amber-400/60 focus:placeholder-amber-400/40"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim()}
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 sm:px-6 text-sm font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              <span className="hidden sm:inline">Send</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}